import type { Popover } from 'driver.js';
import type { Tutorial, TutorialStep } from '@/types/tutorials';
import type { TourElements } from './tourElements';

// Re-export TourElements from organized file
export type { TourElements } from './tourElements';

/**
 * Helper function to check if a tutorial name is an onboarding tutorial
 * Can be used outside of React hooks
 */
export const isOnboardingTutorial = (tutorialName: string | null): boolean => {
	if (!tutorialName) return false;
	return (
		tutorialName === 'OnboardingUser' ||
		tutorialName === 'OnboardingWCTutorial' ||
		tutorialName === 'OnboardingSalesRepTutorial' ||
		tutorialName === 'OnboardingTutorialGraftLogs' ||
		tutorialName === 'OnboardingTutorialPaymentMSL' ||
		tutorialName === 'OnboardingMindsightTutorial'
	);
};

export const isInvoicesTutorial = (tutorialName: string | null): boolean => {
	if (!tutorialName) return false;
	return (
		tutorialName === 'InvoicesTutorial' ||
		tutorialName === 'InvoicesTutorialWC' ||
		tutorialName === 'InvoicesTutorialSalesRep' ||
		tutorialName === 'InvoicesTutorialMindsight'
	);
};

export interface ExtendedPopover extends Popover {
	subtitle?: string;
}

export type TourElementKey = keyof TourElements;

export interface StepConfig {
	reference: string;
	route: string;
	popover: ExtendedPopover;
	permissions?: string[];
	baseTitle: string;
	onNextAction?: string;
	onPrevAction?: string;
}
/**
 * Maps a TutorialStep from backend to StepConfig
 */
const mapTutorialStepToStepConfig = (step: TutorialStep): StepConfig => {
	// Normalize reference: backend may send string or string[]
	const rawReference = step.reference;
	const normalizedReference = Array.isArray(rawReference)
		? (rawReference[0] ?? '')
		: rawReference;
	// Ensure we always work with a clean string key (no spaces, no undefined)
	const referenceKey = String(normalizedReference || '').trim();

	// Build popover object from step fields with default values
	const popover: ExtendedPopover = {
		title: step.title,
		description: step.description,
		side: 'over',
		align: 'end',
		...(step.subtitle && { subtitle: step.subtitle }),
	};

	return {
		reference: referenceKey,
		route: step.route,
		popover,
		permissions: step.permissions,
		baseTitle: step.title,
		onNextAction: (step as TutorialStep & { onNextAction?: string }).onNextAction,
		onPrevAction: (step as TutorialStep & { onPrevAction?: string }).onPrevAction,
	};
};

/**
 * Maps Tutorial from backend to StepConfig array
 */
export const mapTutorialToStepConfigs = (tutorial: Tutorial): StepConfig[] => {
	return tutorial.steps.map(mapTutorialStepToStepConfig);
};

/**
 * Maps array of Tutorials to a Record keyed by tutorial _id
 */
export const mapTutorialsToStepConfigsMap = (
	tutorials: Tutorial[],
): Record<string, StepConfig[]> => {
	const result: Record<string, StepConfig[]> = {};
	for (const tutorial of tutorials) {
		result[tutorial._id] = mapTutorialToStepConfigs(tutorial);
	}
	return result;
};

// Re-export mock data functions from organized files
export { generateMockBVData } from './mockData';

/**
 * Function to close all open components (modals, dialogs, menus)
 * This function handles closing various UI components by:
 * - Clicking close triggers
 * - Dispatching Escape key events
 * - Finding and hiding open menus by z-index and content
 * - Dispatching click events to close menus
 * - Dispatching custom tour drawer close events
 */
export const closeAllOpenComponents = () => {
	const closeTriggers = document.querySelectorAll('[data-part="close-trigger"]');
	closeTriggers.forEach((trigger) => {
		const button = trigger as HTMLElement;
		button?.click?.();
	});

	const escapeEvent = new KeyboardEvent('keydown', {
		key: 'Escape',
		code: 'Escape',
		keyCode: 27,
		which: 27,
		bubbles: true,
		cancelable: true,
	});
	document.dispatchEvent(escapeEvent);

	const allDivs = document.querySelectorAll('div');
	const openMenus: HTMLElement[] = [];

	for (const div of Array.from(allDivs)) {
		const style = window.getComputedStyle(div);
		const zIndex = parseInt(style.zIndex || '0', 10);
		if (
			zIndex >= 1500 &&
			parseFloat(style.borderRadius) > 0 &&
			style.display !== 'none' &&
			style.visibility !== 'hidden' &&
			(div.textContent?.includes('Edit') ||
				div.textContent?.includes('View IVR') ||
				div.textContent?.includes('Order Now') ||
				div.textContent?.includes('Details') ||
				div.textContent?.includes('Support Ticket') ||
				div.textContent?.includes('Void Now') ||
				div.textContent?.includes('Add to Cart'))
		) {
			openMenus.push(div as HTMLElement);
		}
	}

	if (openMenus.length > 0) {
		openMenus.forEach((menu) => {
			(menu as HTMLElement).style.display = 'none';
		});
	}

	const clickEvent = new MouseEvent('click', {
		bubbles: true,
		cancelable: true,
	});
	document.body.dispatchEvent(clickEvent);

	try {
		window.dispatchEvent(new CustomEvent('tour:closeDrawer'));
	} catch (error) {
		console.error('[Tour] Error closing drawer:', error);
	}
};